import { useState, useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import {
  useProjectForecasts,
  useUpdateForecast,
  generateMonthRange,
  formatMonth,
  type ProjectForecast,
} from '@/hooks/useForecasts';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

interface ForecastEditorProps {
  projectId: string;
  currency?: 'GBP' | 'USD' | 'EUR';
  startMonth?: string; // YYYY-MM format
  engagementStartDate?: string | null;
}

interface ForecastRow {
  month: string;
  forecastDays: number;
  forecastValue: number;
  actualDays: number;
  actualValue: number;
  notes: string;
  hasData: boolean;
}

export function ForecastEditor({
  projectId,
  currency = 'USD',
  startMonth,
  engagementStartDate,
}: ForecastEditorProps) {
  const { data, isLoading, error } = useProjectForecasts(projectId);
  const updateForecast = useUpdateForecast();

  // Track which cells are being edited (used for styling active cell)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_editingCell, setEditingCell] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<ForecastRow>>>({});

  // Determine start month
  const effectiveStartMonth = useMemo(() => {
    if (startMonth) return startMonth;
    if (engagementStartDate) {
      return engagementStartDate.substring(0, 7); // Extract YYYY-MM
    }
    // Default to current month
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, [startMonth, engagementStartDate]);

  // Generate 12 months
  const months = useMemo(() => generateMonthRange(effectiveStartMonth, 12), [effectiveStartMonth]);

  // Convert forecasts to a map for easy lookup
  const forecastMap = useMemo(() => {
    const map = new Map<string, ProjectForecast>();
    if (data?.forecasts) {
      for (const f of data.forecasts) {
        map.set(f.month, f);
      }
    }
    return map;
  }, [data]);

  // Build row data for each month
  const rows: ForecastRow[] = useMemo(() => {
    return months.map((month) => {
      const forecast = forecastMap.get(month);
      const pending = pendingChanges[month];
      return {
        month,
        forecastDays: pending?.forecastDays ?? forecast?.forecastDays ?? 0,
        forecastValue: pending?.forecastValue ?? forecast?.forecastValue ?? 0,
        actualDays: pending?.actualDays ?? forecast?.actualDays ?? 0,
        actualValue: pending?.actualValue ?? forecast?.actualValue ?? 0,
        notes: pending?.notes ?? forecast?.notes ?? '',
        hasData: !!forecast,
      };
    });
  }, [months, forecastMap, pendingChanges]);

  const formatCurrency = useCallback(
    (cents: number) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    },
    [currency]
  );

  const handleCellChange = useCallback(
    (month: string, field: keyof ForecastRow, value: string) => {
      const numValue = parseFloat(value) || 0;
      // For value fields, convert to cents
      const actualValue = field.includes('Value') ? Math.round(numValue * 100) : numValue;

      setPendingChanges((prev) => ({
        ...prev,
        [month]: {
          ...prev[month],
          [field]: actualValue,
        },
      }));
    },
    []
  );

  const handleSave = useCallback(
    async (month: string) => {
      const pending = pendingChanges[month];
      if (!pending) return;

      const row = rows.find((r) => r.month === month);
      if (!row) return;

      await updateForecast.mutateAsync({
        projectId,
        month,
        forecastDays: row.forecastDays,
        forecastValue: row.forecastValue,
        actualDays: row.actualDays,
        actualValue: row.actualValue,
        notes: row.notes || undefined,
      });

      // Clear pending changes for this month
      setPendingChanges((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [month]: _, ...rest } = prev;
        return rest;
      });
      setEditingCell(null);
    },
    [pendingChanges, rows, projectId, updateForecast]
  );

  const handleCellBlur = useCallback(
    (month: string) => {
      if (pendingChanges[month]) {
        handleSave(month);
      }
      setEditingCell(null);
    },
    [pendingChanges, handleSave]
  );

  // Calculate variance percentage
  const getVariance = (forecast: number, actual: number): number | null => {
    if (forecast === 0) return null;
    return ((actual - forecast) / forecast) * 100;
  };

  const getVarianceIcon = (variance: number | null) => {
    if (variance === null) return <Minus className="h-3.5 w-3.5 text-gray-400" />;
    if (variance > 5) return <TrendingUp className="h-3.5 w-3.5 text-red-500" />;
    if (variance < -5) return <TrendingDown className="h-3.5 w-3.5 text-green-500" />;
    return <Minus className="h-3.5 w-3.5 text-gray-400" />;
  };

  const getVarianceColor = (variance: number | null): string => {
    if (variance === null) return 'text-gray-400';
    if (variance > 5) return 'text-red-600';
    if (variance < -5) return 'text-green-600';
    return 'text-gray-600';
  };

  // Calculate totals
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        forecastDays: acc.forecastDays + row.forecastDays,
        forecastValue: acc.forecastValue + row.forecastValue,
        actualDays: acc.actualDays + row.actualDays,
        actualValue: acc.actualValue + row.actualValue,
      }),
      { forecastDays: 0, forecastValue: 0, actualDays: 0, actualValue: 0 }
    );
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="sm" />
        <span className="ml-2 text-sm text-gray-500">Loading forecasts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load forecasts</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Monthly Forecasts</h3>
        <span className="text-xs text-gray-500">
          Click cells to edit. Changes auto-save on blur.
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Month
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Forecast Days
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Forecast Value
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actual Days
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Actual Value
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                Var %
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((row) => {
              const variance = getVariance(row.forecastValue, row.actualValue);
              const hasPending = !!pendingChanges[row.month];

              return (
                <tr
                  key={row.month}
                  className={cn(
                    'hover:bg-gray-50',
                    hasPending && 'bg-yellow-50'
                  )}
                >
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {formatMonth(row.month)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      className="w-20 rounded border border-transparent bg-transparent px-2 py-1 text-right text-sm hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={row.forecastDays || ''}
                      placeholder="0"
                      onChange={(e) => handleCellChange(row.month, 'forecastDays', e.target.value)}
                      onFocus={() => setEditingCell(`${row.month}-forecastDays`)}
                      onBlur={() => handleCellBlur(row.month)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="100"
                      min="0"
                      className="w-24 rounded border border-transparent bg-transparent px-2 py-1 text-right text-sm hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={row.forecastValue ? (row.forecastValue / 100).toFixed(0) : ''}
                      placeholder="0"
                      onChange={(e) => handleCellChange(row.month, 'forecastValue', e.target.value)}
                      onFocus={() => setEditingCell(`${row.month}-forecastValue`)}
                      onBlur={() => handleCellBlur(row.month)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      className="w-20 rounded border border-transparent bg-transparent px-2 py-1 text-right text-sm hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={row.actualDays || ''}
                      placeholder="0"
                      onChange={(e) => handleCellChange(row.month, 'actualDays', e.target.value)}
                      onFocus={() => setEditingCell(`${row.month}-actualDays`)}
                      onBlur={() => handleCellBlur(row.month)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="100"
                      min="0"
                      className="w-24 rounded border border-transparent bg-transparent px-2 py-1 text-right text-sm hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={row.actualValue ? (row.actualValue / 100).toFixed(0) : ''}
                      placeholder="0"
                      onChange={(e) => handleCellChange(row.month, 'actualValue', e.target.value)}
                      onFocus={() => setEditingCell(`${row.month}-actualValue`)}
                      onBlur={() => handleCellBlur(row.month)}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {getVarianceIcon(variance)}
                      <span className={cn('text-xs', getVarianceColor(variance))}>
                        {variance !== null ? `${variance.toFixed(0)}%` : '-'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-sm hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={row.notes || ''}
                      placeholder="Notes..."
                      onChange={(e) => handleCellChange(row.month, 'notes', e.target.value)}
                      onFocus={() => setEditingCell(`${row.month}-notes`)}
                      onBlur={() => handleCellBlur(row.month)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 font-medium">
            <tr>
              <td className="px-3 py-2 text-sm">Total</td>
              <td className="px-3 py-2 text-right text-sm">{totals.forecastDays.toFixed(1)}</td>
              <td className="px-3 py-2 text-right text-sm">{formatCurrency(totals.forecastValue)}</td>
              <td className="px-3 py-2 text-right text-sm">{totals.actualDays.toFixed(1)}</td>
              <td className="px-3 py-2 text-right text-sm">{formatCurrency(totals.actualValue)}</td>
              <td className="px-3 py-2 text-center">
                <span
                  className={cn(
                    'text-xs',
                    getVarianceColor(getVariance(totals.forecastValue, totals.actualValue))
                  )}
                >
                  {totals.forecastValue > 0
                    ? `${getVariance(totals.forecastValue, totals.actualValue)?.toFixed(0)}%`
                    : '-'}
                </span>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <div className="rounded-lg bg-blue-50 p-3">
          <p className="text-xs text-blue-600">Forecast Days</p>
          <p className="text-lg font-semibold text-blue-900">{totals.forecastDays.toFixed(1)}</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-3">
          <p className="text-xs text-blue-600">Forecast Value</p>
          <p className="text-lg font-semibold text-blue-900">{formatCurrency(totals.forecastValue)}</p>
        </div>
        <div className="rounded-lg bg-green-50 p-3">
          <p className="text-xs text-green-600">Actual Days</p>
          <p className="text-lg font-semibold text-green-900">{totals.actualDays.toFixed(1)}</p>
        </div>
        <div className="rounded-lg bg-green-50 p-3">
          <p className="text-xs text-green-600">Actual Value</p>
          <p className="text-lg font-semibold text-green-900">{formatCurrency(totals.actualValue)}</p>
        </div>
      </div>
    </div>
  );
}
